import React, { useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { type Location, useLocation, useParams } from 'react-router-dom';
import { type RectReadOnly } from 'react-use-measure';
import { useGetFactoriesQuery } from '@api/queries/factory';
import { materialsList } from '@common/constants';
import { useCheckRequirements } from '@common/hooks/check-requirements';
import { useMaterialProduction } from '@common/hooks/material-production';
import { firebaseLogCollectResource } from '@common/lib/firebaseLog';
import { getImage } from '@common/lib/images';
import { getText } from '@common/lib/text';
import { ModalContext } from '@components/Modals/Modal/config';
import { ResourcesNavigation } from '@components/Resources/ResourcesNavigation/ResourcesNavigation';
import { PageBackground } from '@components/ui/Backgrounds/PageBackground/PageBackground';
import {
  boostFactoryTimersEnd,
  selectFactoriesAsObject,
  selectFactoryByIdOrName,
} from '@redux/selectors/factoryReducer';
import { useAppSelector } from '@redux/store';
import classNames from 'classnames';

import { useExtendedInfoQuery } from '@/api/queries/user';
import { useGetWorkshopQuery } from '@/api/queries/workshop';
import { useCountDownBoostsTimers } from '@/common/hooks/count-down-boosts-timers';
import { GoodsNavigation } from '@/components/Workshop/GoodsNavigation/GoodsNavigation';

import { FactoryProduction } from '../FactoryProduction/FactoryProduction';
import { FactoryTitle } from '../FactoryTitle/FactoryTitle';

import { ProductionInfo } from './ProductionInfo/ProductionInfo';

import css from './styles.module.scss';

const Production: React.FC = () => {
  const { id } = useParams();
  useGetFactoriesQuery(id, { refetchOnMountOrArgChange: true });
  useGetWorkshopQuery();
  useExtendedInfoQuery();
  const { openModal } = useContext(ModalContext);
  const [toAdd, setToAdd] = useState<number>();
  const [pullCoords, setPullCoords] = useState<RectReadOnly | null>(null);
  const [pullAnimating, setPullAnimating] = useState(false);
  const { t } = useTranslation();

  const factory = useAppSelector((state) => selectFactoryByIdOrName(state, id));
  const buildsData = useAppSelector(selectFactoriesAsObject);
  const nextToBuild = useAppSelector((state) => state.factory?.nextToBuild);
  const producted = useMaterialProduction(factory);
  const { isComplete } = useCheckRequirements(factory?.nextLevelRequirements);
  const storage = useAppSelector((state) => state.user.user?.storage);
  const previousValueRef = useRef(storage);
  const location = useLocation() as Location<{ modal?: 'boostFactory' }>;
  const isTimersEnd = useAppSelector(boostFactoryTimersEnd);
  useCountDownBoostsTimers(!isTimersEnd, 'Factory');

  useEffect(() => {
    if (previousValueRef.current !== storage && factory) {
      firebaseLogCollectResource(factory, storage, previousValueRef.current);
      previousValueRef.current = storage;
    }
  }, [storage]);

  useEffect(() => {
    if (location?.state?.modal && factory) {
      openModal({
        renderKind: location.state.modal,
        props: {
          plantId: factory.id,
          plantType: 'Factory',
          plant: factory.name,
          item: factory.name,
          level: factory.level,
        },
      });
    }
  }, [location?.state?.modal, factory]);

  if (!factory) {
    return null;
  }

  const background = getImage({ name: factory.name, field: 'background' });
  const title = getText({ name: factory.name, kind: 'build', t });

  const openBoostFactory = (): void => {
    openModal({
      renderKind: 'boostFactory',
      props: {
        plantId: factory.id,
        plant: factory.name,
        item: factory.name,
        plantType: 'Factory',
        level: factory.level,
      },
    });
  };

  return (
    <main className="relative h-full">
      <Helmet>
        <title>Production</title>
      </Helmet>
      {createPortal(
        <GoodsNavigation material={factory.name} />,
        document.getElementById('toolbar-item-2') as Element,
      )}
      <PageBackground
        backgroundImage={{
          png: background?.img || '',
          webp: background?.webp || '',
        }}
        variant="page"
        className={classNames(
          'absolute top-0 left-0 h-[47.52vh]',
          css.gradient,
        )}
      />
      <div className={css.content}>
        <FactoryTitle>{title}</FactoryTitle>
        <div className="w-full flex flex-col items-center">
          {id && (
            <ProductionInfo
              plantId={factory.id}
              producted={producted}
              source={factory.source}
              material={factory.name}
              incommingStorage={factory.incommingStorage}
              productionRateOriginal={factory.productionRateOriginal}
              productionRate={factory.rate}
              toAdd={toAdd}
              updatedAt={factory.updatedAt}
              pullCoords={pullCoords}
              pullAnimating={pullAnimating}
              setPullAnimating={setPullAnimating}
              timeLeft={factory.productionTimeLeft}
            />
          )}
          <div className="w-full px-4 mb-8">
            <FactoryProduction
              plantId={factory.id}
              openBoostFactory={openBoostFactory}
              isComplete={isComplete}
              limit={factory.storageCapacity}
              toAdd={toAdd}
              producted={producted}
              incommingStorage={factory.incommingStorage}
              setToAdd={setToAdd}
              localStock={factory.localStock}
              setPullCoords={setPullCoords}
              setPullAnimating={setPullAnimating}
            />
          </div>
          <ResourcesNavigation
            data={{
              list: materialsList,
              buildsData,
              nextToBuild: nextToBuild || undefined,
            }}
          />
        </div>
      </div>
    </main>
  );
};

export default Production;
